import { FC, useRef } from "react";
import { TVacancyDetail } from "../../../../store/vacancy/types";
import ActionDropdown from "../../../UI/ActionDropdown";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Block from "../../../UI/Icons/Block";
import Copy from "../../../UI/Icons/Copy";
import EditPencil from "../../../UI/Icons/EditPencil";
import Eye from "../../../UI/Icons/Eye";
import VerticalDots from "../../../UI/Icons/VerticalDots";
import VacancyPosition from "../../../UI/VacancyPosition";
import CentreImage from "../../../Vacancy/CentreImage";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";
import VacancySettings from "../../../Vacancy/VacancySettings";

interface PendingCardProps {
  vacancy: TVacancyDetail;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  onViewDetails: () => void;
  handleEdit: () => void;
  handleDuplicate: () => void;
  handleCancel: () => void;
}

const PendingCard: FC<PendingCardProps> = ({
  vacancy,
  onViewDetails,
  showDropdown,
  setShowDropdown,
  handleEdit,
  handleDuplicate,
  handleCancel,
}) => {
  const formattedTimeframes = formatTimeframes(vacancy.timeframes);
  const dropdownRef = useRef<HTMLButtonElement | null>(null);

  const dropdownItems = [
    ...(vacancy?.applications_count > 0 || !vacancy.isEditableByCentre ? [] : [{ // If has applications or not editable by centre, remove edit option
      label: "Edit Vacancy",
      icon: <EditPencil />,
      onClick: handleEdit,
    }]),
    {
      label: "Duplicate as New Vacancy",
      icon: <Copy />,
      onClick: handleDuplicate,
    },
    {
      label: "Cancel Vacancy",
      icon: <Block />,
      onClick: handleCancel,
    },
  ];

  return (
    <div className="bg-white p-15px d-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black">
      <div className="flex-grow-1 w-100 text-break">
        <div className="d-flex gap-20 align-items-center">
          <div className="w-50px h-50px radius-50px">
            <CentreImage vacancy={vacancy} height="50px" width="50px" />
          </div>
          <div className="flex-grow-1 d-flex gap-10 justify-content-between">
            <div className="d-flex flex-column gap-3 text-black">
              <div className="fs-16 fw-700">
                <VacancyPosition
                  positions={vacancy?.vacancy_positions}
                  alternatePosition={vacancy?.alternate_position_legacy || ""}
                />
              </div>
              <div className="fs-13 fw-400">{`${vacancy?.centre?.business_name}, ${vacancy?.centre?.address}`}</div>
            </div>
            <div className="min-w-24px position-relative d-block d-lg-none">
              <CustomButton
                variant={"primary"}
                type="button"
                ref={dropdownRef}
                className="btn-theme bg-white p-0 h-auto"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <VerticalDots color={!showDropdown ? "#aaaaaa" : "#000000"} />
              </CustomButton>
              <ActionDropdown
                options={dropdownItems}
                isOpen={showDropdown}
                onClose={() => setShowDropdown(false)}
                toggleRef={dropdownRef}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="min-w-380px w-380px mobile-w-100 text-break">
        {formattedTimeframes.map((ftf, i) => (
          <div
            key={i}
            className={`fs-13 ${ftf.alert ? "text-alert fw-700" : "fw-400"}`}
          >
            {ftf.label}
          </div>
        ))}
      </div>
      <div className="min-w-120px">
        <div className="fs-13">{vacancy?.posted}</div>
      </div>
      <div className="min-w-155px w-155px mobile-w-100 d-flex gap-5 flex-wrap text-break">
        <VacancySettings vacancy={vacancy} />
      </div>
      <div className="min-w-165px mobile-w-100">
        <CustomButton
          variant="primary"
          type="button"
          className="btn-theme bg-off-white text-black w-100"
          onClick={onViewDetails}
        >
          <Eye color="black" />
          View Details
        </CustomButton>
      </div>
      <div className="min-w-24px position-relative d-none d-lg-block">
        <CustomButton
          variant={"primary"}
          type="button"
          ref={dropdownRef}
          className="btn-theme bg-white p-0 h-auto"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <VerticalDots color={!showDropdown ? "#aaaaaa" : "#000000"} />
        </CustomButton>
        <ActionDropdown
          options={dropdownItems}
          isOpen={showDropdown}
          onClose={() => setShowDropdown(false)}
          toggleRef={dropdownRef}
        />
      </div>
    </div>
  );
};

export default PendingCard;
